import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { CourseScheduleService } from "src/app/shared/services/course-schedule/course-schedule.service";
import {
  courseScheduleListFailure,
  courseScheduleListLoad,
  courseScheduleListLoadSuccess,
} from "../../actions/course-schedule/get-course-schedule.actions";
import {
  createScheduleLoad,
  createScheduleLoadFailure,
  createScheduleLoadSuccess,
} from "../../actions/course-schedule/create-course-schedule.actions";
import {
  editScheduleLoad,
  editScheduleLoadSuccess,
} from "../../actions/course-schedule/edit-course-schedule.actions";
import {
  editTopicScheduleLoad,
  editTopicScheduleLoadFailure,
  editTopicScheduleLoadSuccess,
} from "../../actions/course-schedule/edit-course-schedule-topic.actions";
import {
  Schedule,
  ScheduleTopic,
} from "src/app/shared/interfaces/course-schedule";
import { Action, Store } from "@ngrx/store";
import { CourseScheduleState } from "../../reducers/course-schedule/course-schedule.reducer";

@Injectable()
export class CourseScheduleEffect {
  getCourseScheduleList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(courseScheduleListLoad),
      switchMap((param) => {
        return this.scheduleService
          .getSchedule(param.CourseId, param.GroupId)
          .pipe(
            map((schedule: Schedule) =>
              courseScheduleListLoadSuccess({ schedule }),
            ),
            catchError(({ error }) => of(courseScheduleListFailure(error))),
          );
      }),
    ),
  );

  editScheduleLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editScheduleLoad),
      switchMap((param) => {
        return this.scheduleService
          .editSchedule(
            param.CourseId,
            param.GroupId,
            param.ScheduleId,
            param.schedule,
          )
          .pipe(
            map((schedule: Schedule) => editScheduleLoadSuccess({ schedule })),
            catchError(({ error }) => of(createScheduleLoadFailure({ error }))),
          );
      }),
    ),
  );

  createScheduleLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createScheduleLoad),
      switchMap((param) => {
        return this.scheduleService
          .createSchedule(param.CourseId, param.GroupId, param.schedule)
          .pipe(
            map((schedule: Schedule) =>
              createScheduleLoadSuccess({ schedule }),
            ),
            catchError(({ error }) => of(createScheduleLoadFailure({ error }))),
          );
      }),
    ),
  );

  editTopicScheduleLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editTopicScheduleLoad),
      withLatestFrom(
        this.store.select((state) => state.courseSchedule.schedule?.id),
      ),
      switchMap(([param, currentScheduleId]) => {
        return this.scheduleService
          .editScheduleTopic(
            param.CourseId,
            param.GroupId,
            param.TopicId,
            param.schedule,
          )
          .pipe(
            mergeMap((schedule: ScheduleTopic) => {
              const actions: Action[] = [
                editTopicScheduleLoadSuccess({ schedule }),
              ];

              if (!currentScheduleId) {
                actions.push(
                  courseScheduleListLoad({
                    CourseId: param.CourseId,
                    GroupId: param.GroupId,
                  }),
                );
              }

              return actions;
            }),
            catchError(({ error }) =>
              of(editTopicScheduleLoadFailure({ error })),
            ),
          );
      }),
    ),
  );

  constructor(
    private scheduleService: CourseScheduleService,
    private actions$: Actions,
    private store: Store<{ courseSchedule: CourseScheduleState }>,
  ) {}
}
